import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  CreditCard,
  Lock,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Unlock,
  UserPlus,
} from 'lucide-react';
import type {
  AdminPlanDto,
  BillingAdminCandidateDto,
  ChargeStatus,
  OneTimeFeeDto,
} from '@mg/shared';
import { MIN_JUSTIFICATION_LENGTH } from '@mg/shared';
import { api, ApiRequestError } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { PageHeader } from '../components/shell/PageHeader';
import { SettingsTabs, type SettingsTab } from '../components/settings/SettingsTabs';
import { useSaveHold } from '../components/settings/footerSave';
import { CHANGES_SAVED, useToast } from '../components/ui/toast';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge, type BadgeTone } from '../components/ui/Badge';
import { FormField, Input, Select, Textarea } from '../components/ui/Field';
import { AlertBanner } from '../components/ui/AlertBanner';
import { Modal } from '../components/ui/Modal';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { ErrorState, LoadingState } from '../components/ui/States';
import { EDGE_BLEED_FRAME, useEdgeBleed } from '../components/ui/useEdgeBleed';
import { formatDateLong, formatDateTimeLong } from '../lib/display';
import { planPriceWithInterval } from '../features/billing/planPrice';

/**
 * `<input type="date">` takes a LOCAL calendar date. Slicing the ISO string instead would print the
 * UTC day, which runs a day ahead for evening timestamps in western zones — a plan started 9pm on
 * the 12th would show as the 13th, disagreeing with the date on the tenant's own Billing page.
 */
function toDateInputValue(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Billing Admin — which plans the organisation is allowed to buy.
 *
 * Deliberately separate from Billing: a Tenant Admin picks from the menu, this sets the menu. The
 * server gates on an email allowlist (`isPlatformBillingAdmin`), so this page only mirrors that decision.
 *
 * Its five sections are tabs (spec "App Settings, Billing Admin and Site Integrations"), and only the
 * selected one shows. There is no sticky Save footer: every control acts at once, as it always has,
 * except Plan start date, which keeps its own Save. The header's Sync plans and the page's banners stay
 * above the tabs. A save that works (Plan start date, Hide / Offer, adding or removing a Billing Admin,
 * Suspend / Restore access) also shows the "Changes saved" toast beside its usual confirmation; Sync
 * plans and the one-time fee actions keep only their banners. A tab switch, or leaving the page, waits
 * for a section's own request still running (useSaveHold), since its answer shows in that section;
 * Hide / Offer and Sync plans answer in the page's banners, which stay.
 */
export function BillingAdminPage() {
  const { principal } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isPlatformBillingAdmin = Boolean(principal?.user?.isPlatformBillingAdmin);

  const plansQuery = useQuery({
    queryKey: ['billing-admin-plans'],
    queryFn: ({ signal }) => api.getAdminPlans(signal),
    enabled: isPlatformBillingAdmin,
  });

  const setExposure = useMutation({
    mutationFn: ({ id, isSelectable }: { id: string; isSelectable: boolean }) =>
      api.setPlanExposure(id, isSelectable),
    onSuccess: (plans, vars) => {
      setError(null);
      setNotice(vars.isSelectable ? 'Plan is now offered on Billing.' : 'Plan is hidden from Billing.');
      queryClient.setQueryData(['billing-admin-plans'], plans);
      // The standard page reads a different list, so refresh it too.
      void queryClient.invalidateQueries({ queryKey: ['billing-plans'] });
      void queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast.show(CHANGES_SAVED);
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'That change could not be saved.'),
  });

  const syncPlans = useMutation({
    mutationFn: () => api.syncBillingPlans(),
    onSuccess: (r) => {
      setError(null);
      setNotice(r.message ?? `Synced ${r.synced} plan${r.synced === 1 ? '' : 's'} from Stripe.`);
      void queryClient.invalidateQueries({ queryKey: ['billing-admin-plans'] });
      void queryClient.invalidateQueries({ queryKey: ['billing-plans'] });
    },
    onError: (err) =>
      setError(err instanceof ApiRequestError ? err.message : 'Sync failed.'),
  });

  // On a desktop screen too narrow for the plans table, its card runs off the page's right edge and
  // the table reaches through the card's padding to that edge, so the cut says it scrolls.
  const plansBleedRef = useEdgeBleed<HTMLDivElement>('marked');

  if (!isPlatformBillingAdmin) {
    return <ErrorState message="You do not have access to Billing Admin." />;
  }
  if (plansQuery.isLoading) return <LoadingState />;
  if (plansQuery.isError) {
    return <ErrorState message="Plans could not be loaded." onRetry={() => void plansQuery.refetch()} />;
  }

  const plans = plansQuery.data ?? [];
  const offered = plans.filter((p) => p.isSelectable && p.stripePriceId).length;

  // In page order. None has the footer (`footer` stays false): see the comment on the page.
  const tabs: SettingsTab[] = [
    { key: 'access', label: 'Billing Admin access', render: () => <BillingAdminAccessCard /> },
    { key: 'tenant-access', label: 'Tenant access', render: () => <TenantAccessCard /> },
    { key: 'start-date', label: 'Plan start date', render: () => <BillingScheduleCard /> },
    { key: 'fees', label: 'One-time fees', render: () => <OneTimeFeesCard /> },
    {
      key: 'plans',
      label: 'Stripe plans',
      // One box for the card and its footnote, so the note keeps its small gap under the card (the
      // panel spaces its own children further apart).
      render: () => (
        <div>
          <Card {...EDGE_BLEED_FRAME}>
            <CardHeader
              title="Stripe plans"
              description={
                offered === 0
                  ? 'Nothing is offered on the Billing page yet — turn a synced plan on below.'
                  : `${offered} plan${offered === 1 ? '' : 's'} offered on the Billing page.`
              }
            />
            <CardBody>
              {plans.length === 0 ? (
                <p className="text-sm text-ink-faint">
                  No plans yet. Create your products in Stripe, then use “Sync plans”.
                </p>
              ) : (
                <div ref={plansBleedRef} className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-line-soft text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
                        <th className="py-2 pr-3">Plan</th>
                        <th className="py-2 pr-3">Price</th>
                        <th className="py-2 pr-3">Stripe</th>
                        <th className="py-2 pr-3">On Billing page</th>
                        <th className="py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((plan) => (
                        <PlanRow
                          key={plan.id}
                          plan={plan}
                          busy={setExposure.isPending}
                          onToggle={(isSelectable) => setExposure.mutate({ id: plan.id, isSelectable })}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          <p className="mt-3 text-xs text-ink-faint">
            Plans with no Stripe price can&apos;t be offered — create the product in Stripe and sync.
            The plan this installation is currently on always stays visible.
          </p>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        icon={ShieldCheck}
        title="Billing Admin"
        description="Choose which Stripe plans appear on the Billing page."
        actions={
          <Button
            variant="secondary"
            size="sm"
            loading={syncPlans.isPending}
            onClick={() => syncPlans.mutate()}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Sync plans
          </Button>
        }
      />

      {/* Above the tabs, so a message stays in view whichever tab is open. */}
      {notice && (
        <div className="mb-4">
          <AlertBanner tone="info">{notice}</AlertBanner>
        </div>
      )}
      {error && (
        <div className="mb-4">
          <AlertBanner tone="warning">{error}</AlertBanner>
        </div>
      )}

      <SettingsTabs label="Billing Admin sections" tabs={tabs} />
    </div>
  );
}

function PlanRow({
  plan,
  busy,
  onToggle,
}: {
  plan: AdminPlanDto;
  busy: boolean;
  onToggle: (isSelectable: boolean) => void;
}) {
  const unsynced = !plan.stripePriceId;
  return (
    <tr className="border-b border-line-soft last:border-b-0">
      <td className="py-2.5 pr-3">
        <span className="font-medium text-ink">{plan.name}</span>
        {plan.isCurrent && (
          <span className="ml-2 inline-block align-middle">
            <Badge tone="green" size="sm">
              Current
            </Badge>
          </span>
        )}
        {!plan.isActive && (
          <span className="ml-2 inline-block align-middle">
            <Badge tone="neutral" size="sm">
              Inactive
            </Badge>
          </span>
        )}
      </td>
      <td className="whitespace-nowrap py-2.5 pr-3 text-ink-label">
        {planPriceWithInterval(plan)}
      </td>
      <td className="py-2.5 pr-3 text-xs text-ink-faint">
        {unsynced ? <span className="text-amber-600 dark:text-warning-dark">Not synced</span> : plan.stripePriceId}
      </td>
      <td className="py-2.5 pr-3">
        {plan.isSelectable ? (
          <Badge tone="green" size="sm">
            Offered
          </Badge>
        ) : (
          <Badge tone="neutral" size="sm">
            Hidden
          </Badge>
        )}
      </td>
      <td className="py-2.5 text-right">
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || unsynced}
          onClick={() => onToggle(!plan.isSelectable)}
        >
          {plan.isSelectable ? 'Hide' : 'Offer'}
        </Button>
      </td>
    </tr>
  );
}


/**
 * Who may reach this page.
 *
 * The candidate list is the Alloterra Entra group, hardcoded in the image — this instance is the
 * "gold" template copied into a resource group per customer, so anything stored per-instance would
 * have to be set up again for every one of them. Membership of that group makes someone ELIGIBLE;
 * access is still granted explicitly here, and only the seed admin has it in a fresh deployment.
 */
function BillingAdminAccessCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const hold = useSaveHold();
  const [pick, setPick] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const accessQuery = useQuery({
    queryKey: ['billing-admin-access'],
    queryFn: ({ signal }) => api.getBillingAdminAccess(signal),
  });

  const onDone = (next: Awaited<ReturnType<typeof api.getBillingAdminAccess>>) => {
    setErr(null);
    setPick('');
    queryClient.setQueryData(['billing-admin-access'], next);
    toast.show(CHANGES_SAVED);
  };
  const onFail = (e: unknown) =>
    setErr(e instanceof ApiRequestError ? e.message : 'That change could not be saved.');

  const grant = useMutation({ mutationFn: (oid: string) => api.grantBillingAdmin(oid), onSuccess: onDone, onError: onFail });
  const revoke = useMutation({ mutationFn: (email: string) => api.revokeBillingAdmin(email), onSuccess: onDone, onError: onFail });

  const access = accessQuery.data;
  const addable: BillingAdminCandidateDto[] = (access?.candidates ?? []).filter((c) => !c.alreadyGranted);

  return (
    <Card>
      <CardHeader
        title="Billing Admin access"
        description="Only these people can open this page. Candidates come from the Alloterra directory group."
      />
      <CardBody>
        <InstallBanner />

        {err && (
          <div className="mb-3">
            <AlertBanner tone="warning">{err}</AlertBanner>
          </div>
        )}
        {access?.directoryError && (
          <div className="mb-3">
            <AlertBanner tone="warning">
              The Alloterra directory group could not be read, so nobody new can be added right now.
              Existing access is unaffected.
            </AlertBanner>
          </div>
        )}

        {accessQuery.isLoading ? (
          <p className="text-sm text-ink-faint">Loading…</p>
        ) : (
          <>
            <ul className="divide-y divide-line-soft">
              {(access?.seedAdmins ?? []).map((email) => (
                <li key={email} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate text-ink">{email}</span>
                    <span className="block truncate text-xs text-ink-faint">
                      Built in to this deployment — cannot be removed from here
                    </span>
                  </span>
                  <Badge tone="neutral" size="sm">Always</Badge>
                </li>
              ))}
              {(access?.granted ?? []).map((g) => (
                <li key={g.email} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate text-ink">{g.displayName ?? g.email}</span>
                    <span className="block truncate text-xs text-ink-faint">{g.email}</span>
                  </span>
                  {g.isSeed ? (
                    <Badge tone="neutral" size="sm">Always</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={revoke.isPending}
                      onClick={() => void hold(revoke.mutateAsync(g.email))}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                      Remove
                    </Button>
                  )}
                </li>
              ))}
              {(access?.granted ?? []).length === 0 && (access?.seedAdmins ?? []).length === 0 && (
                <li className="py-2 text-sm text-ink-faint">Nobody has been added yet.</li>
              )}
            </ul>

            <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-line-soft pt-4">
              <div className="min-w-[16rem] flex-1">
                <Select
                  aria-label="Add a Billing Admin"
                  value={pick}
                  onChange={(e) => setPick(e.target.value)}
                  disabled={addable.length === 0}
                >
                  <option value="">
                    {addable.length === 0 ? 'Everyone in the group already has access' : 'Choose someone…'}
                  </option>
                  {addable.map((c) => (
                    <option key={c.oid} value={c.oid}>
                      {c.displayName} — {c.email}
                    </option>
                  ))}
                </Select>
              </div>
              <Button
                size="sm"
                disabled={!pick || grant.isPending}
                loading={grant.isPending}
                onClick={() => void hold(grant.mutateAsync(pick))}
              >
                <UserPlus className="h-4 w-4" aria-hidden />
                Add
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}


/**
 * WHOSE billing this page is showing.
 *
 * From the owner's review: "the Billing Admin access section should be clearer who the tenant is."
 * A vendor administrator opens identical-looking copies of this page across customer installs, and
 * until now nothing on it named the customer — so the only way to tell them apart was the address
 * bar. None of this is secret; it is the customer describing itself.
 *
 * Deliberately quiet if the request fails: this is orientation, and an unavailable label must not
 * put an error box above the access list it is introducing.
 */
function InstallBanner() {
  const q = useQuery({
    queryKey: ['billing-admin', 'install'],
    queryFn: ({ signal }) => api.getInstallIdentity(signal),
    staleTime: 60_000,
  });
  const d = q.data;
  if (!d) return null;
  return (
    <div className="mb-4 rounded-lg border border-line-soft bg-surface-sunken px-3 py-2">
      <p className="text-sm font-semibold text-ink">{d.host}</p>
      <dl className="mt-1 grid gap-x-6 gap-y-0.5 text-xs text-ink-muted sm:grid-cols-2">
        {d.workspaces.length > 0 && (
          <div className="flex gap-1">
            <dt className="text-ink-faint">Brand Workspaces:</dt>
            <dd className="min-w-0 truncate text-ink-label">{d.workspaces.join(', ')}</dd>
          </div>
        )}
        <div className="flex gap-1">
          <dt className="text-ink-faint">Signs in with:</dt>
          <dd className="text-ink-label">
            {d.customerDirectory === 'google'
              ? `Google Workspace${d.customerDomains.length > 0 ? ` (${d.customerDomains.join(', ')})` : ''}`
              : 'Microsoft 365'}
          </dd>
        </div>
        <div className="flex gap-1">
          <dt className="text-ink-faint">Billable seats:</dt>
          <dd className="text-ink-label">{d.seats}</dd>
        </div>
      </dl>
    </div>
  );
}

/** Money renders to the CENT — an invoice is not a whole-dollar figure. */
function money(cents: number, currency: string): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  });
}

/**
 * When the first monthly charge falls.
 *
 * Settable only BEFORE a subscription exists: Stripe fixes a subscription's billing cycle at
 * creation, so offering the control afterwards would be a field that silently does nothing.
 */
function BillingScheduleCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const hold = useSaveHold();
  const [date, setDate] = useState('');
  const hydrated = useRef(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['billing-schedule'],
    queryFn: ({ signal }) => api.getBillingSchedule(signal),
  });

  // Hydrate once, so a refetch can't overwrite a date being typed.
  useEffect(() => {
    if (!q.data || hydrated.current) return;
    hydrated.current = true;
    // Before checkout the box holds the REQUESTED first-charge date. Afterwards Stripe owns the
    // dates and the box is read-only, so it must show the date the plan actually started — a stale
    // anchor left over from the request would contradict the banner right above it.
    setDate(
      toDateInputValue(
        q.data.editable ? q.data.billingCycleAnchor : (q.data.planStartedAt ?? q.data.billingCycleAnchor),
      ),
    );
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      api.setBillingSchedule(date ? new Date(date + 'T00:00:00').toISOString() : null),
    onSuccess: (next) => {
      setErr(null);
      setOk(
        next.billingCycleAnchor
          ? 'Start date saved.'
          : 'Start date cleared — checkout will charge immediately.',
      );
      queryClient.setQueryData(['billing-schedule'], next);
      toast.show(CHANGES_SAVED);
    },
    onError: (e) =>
      setErr(e instanceof ApiRequestError ? e.message : 'That date could not be saved.'),
  });

  const editable = q.data?.editable ?? false;

  return (
    <Card>
      <CardHeader
        title="Plan start date"
        description="When the first monthly charge should be taken from the tenant's card."
      />
      <CardBody>
        {err && (
          <div className="mb-3">
            <AlertBanner tone="warning">{err}</AlertBanner>
          </div>
        )}
        {ok && (
          <div className="mb-3">
            <AlertBanner tone="info">{ok}</AlertBanner>
          </div>
        )}
        {q.data && !editable && (
          <div className="mb-3">
            <AlertBanner tone="warning">
              {q.data.planStartedAt
                ? `The plan started ${formatDateLong(q.data.planStartedAt)}${
                    q.data.nextChargeAt
                      ? `, and the next charge falls on ${formatDateLong(q.data.nextChargeAt)}`
                      : ''
                  }. Stripe fixed the cycle when the subscription was created — change it from the billing portal.`
                : 'A subscription already exists, so Stripe has fixed its billing cycle. Change it from the billing portal instead.'}
            </AlertBanner>
          </div>
        )}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-52">
            <Input
              type="date"
              aria-label={editable ? 'First charge date' : 'Plan start date'}
              value={date}
              disabled={!editable}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <Button size="sm" disabled={!editable} loading={save.isPending} onClick={() => void hold(save.mutateAsync())}>
            <CalendarClock className="h-4 w-4" aria-hidden />
            Save
          </Button>
        </div>
        <p className="mt-2 text-xs text-ink-faint">
          {editable
            ? 'Leave empty to charge as soon as the tenant completes checkout. A date here delays the first charge to that day and runs the monthly cycle from it.'
            : 'The date the plan started. The monthly charge runs from it.'}
        </p>
      </CardBody>
    </Card>
  );
}

const CHARGE_STATUS_LABEL: Record<ChargeStatus, string> = {
  PENDING: 'In progress',
  PAID: 'Paid',
  FAILED: 'Failed',
  VOIDED: 'Cancelled',
  NEEDS_REVIEW: 'Needs review',
};

const CHARGE_STATUS_TONE: Record<ChargeStatus, BadgeTone> = {
  PENDING: 'amber',
  PAID: 'green',
  FAILED: 'red',
  // Cancelled is not a failure and not a payment — it should read as neither.
  VOIDED: 'neutral',
  NEEDS_REVIEW: 'amber',
};

/**
 * Withhold the app pending payment — a decision, never automatic.
 *
 * A failed payment on its own does nothing: the vendor looks at the situation and pulls this, and
 * can put it back the same way. While it is on, every page in the tenant's app is blurred behind an
 * explanation and the API refuses writes; the Billing page stays reachable so they can pay, and
 * vendor staff are exempt so this card never locks itself away.
 */
function TenantAccessCard() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const hold = useSaveHold();
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['tenant-access'],
    queryFn: ({ signal }) => api.getTenantAccess(signal),
  });

  const save = useMutation({
    mutationFn: (vars: { suspended: boolean; note?: string }) =>
      api.setTenantAccess(vars.suspended, vars.note),
    onSuccess: (next) => {
      setErr(null);
      setConfirming(false);
      setNote('');
      queryClient.setQueryData(['tenant-access'], next);
      toast.show(CHANGES_SAVED);
    },
    onError: (e) => {
      setConfirming(false);
      setErr(e instanceof ApiRequestError ? e.message : 'That could not be changed.');
    },
  });

  const suspended = q.data?.suspended ?? false;

  return (
    <Card>
      <CardHeader
        title="Tenant access"
        description="Withhold the app until billing is settled. Nothing is deleted and access returns the moment you restore it."
      />
      <CardBody>
        {err && (
          <div className="mb-3">
            <AlertBanner tone="warning">{err}</AlertBanner>
          </div>
        )}

        {suspended ? (
          <div className="space-y-3">
            <AlertBanner tone="warning">
              Access is suspended{q.data?.suspendedBy ? ` by ${q.data.suspendedBy}` : ''}
              {q.data?.suspendedAt ? ` on ${formatDateTimeLong(q.data.suspendedAt)}` : ''}. Everyone
              in this installation sees a payment notice instead of the app.
            </AlertBanner>
            {q.data?.note && (
              <p className="text-sm text-ink-label">
                Message shown to them: “{q.data.note}”
              </p>
            )}
            <Button
              variant="secondary"
              size="sm"
              loading={save.isPending}
              onClick={() => void hold(save.mutateAsync({ suspended: false }))}
            >
              <Unlock className="h-4 w-4" aria-hidden />
              Restore access
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {/* The owner opened this page, saw only a Suspend button, and could not tell whether
                the tenant was active. Nothing said so, because "not suspended" was represented by
                the ABSENCE of a warning. State it. */}
            <AlertBanner tone="info">
              <span className="inline-flex items-center gap-2">
                <Badge tone="green" size="sm">
                  ACTIVE
                </Badge>
                <span className="font-semibold">This tenant can use the app right now.</span>
              </span>{' '}
              Access does not depend on payment: a missing or invalid card, a failed charge, or no
              subscription at all leave the app fully usable. Only suspending it here blocks anyone.
            </AlertBanner>
            <FormField
              label="Message shown behind the lock (optional)"
              htmlFor="suspend-note"
              hint="Leave empty for the standard payment notice."
            >
              <Textarea
                id="suspend-note"
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. The July invoice is 30 days overdue. Contact accounts@… to restore service."
              />
            </FormField>
            <Button variant="danger" size="sm" onClick={() => setConfirming(true)}>
              <Lock className="h-4 w-4" aria-hidden />
              Suspend access
            </Button>
          </div>
        )}
      </CardBody>

      <ConfirmModal
        open={confirming}
        title="Suspend access for this tenant?"
        danger
        confirmLabel="Suspend access"
        cancelLabel="Not now"
        busy={save.isPending}
        message={
          <>
            Every user in this installation will find the app blurred behind a payment notice, and
            changes will be refused by the API. Their data is untouched, the Billing page stays open
            so they can pay, and you can restore access here at any time.
          </>
        }
        onClose={() => setConfirming(false)}
        onConfirm={() => void hold(save.mutateAsync({ suspended: true, note: note.trim() || undefined }))}
      />
    </Card>
  );
}

/**
 * One-off fees (onboarding, setup).
 *
 * Never shown on the tenant's Billing page — the vendor applies them to the card the tenant already
 * saved. Apply stays clickable in every state, but it opens a dialog rather than charging on the
 * spot: the server decides whether the fee may be charged, and the dialog explains a refusal.
 * Charging past an unpaid invoice, or repeating a paid fee by reflex, is how a customer gets billed
 * twice — so a repeat needs a written reason and an unresolved invoice must be dealt with first.
 */
function OneTimeFeesCard() {
  const queryClient = useQueryClient();
  const hold = useSaveHold();
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [pendingFee, setPendingFee] = useState<OneTimeFeeDto | null>(null);
  const [cancelling, setCancelling] = useState<{ fee: OneTimeFeeDto; invoiceId: string } | null>(
    null,
  );
  const [resolving, setResolving] = useState<{ fee: OneTimeFeeDto; chargeId: string } | null>(null);

  const q = useQuery({
    queryKey: ['billing-fees'],
    queryFn: ({ signal }) => api.getOneTimeFees(signal),
  });

  const charge = useMutation({
    mutationFn: (vars: { planId: string; justification?: string }) =>
      api.chargeOneTimeFee(vars.planId, vars.justification),
    onSuccess: (fees) => {
      setErr(null);
      setOk('Charged. The invoice is in the billing history.');
      setPendingFee(null);
      queryClient.setQueryData(['billing-fees'], fees);
      void queryClient.invalidateQueries({ queryKey: ['billing-invoices'] });
    },
    onError: (e) => {
      setOk(null);
      setErr(e instanceof ApiRequestError ? e.message : 'The charge could not be completed.');
      setPendingFee(null);
      // The attempt is recorded server-side either way, so refresh to show it and the new gate.
      void queryClient.invalidateQueries({ queryKey: ['billing-fees'] });
    },
  });

  const resolveCharge = useMutation({
    mutationFn: (vars: { planId: string; chargeId: string }) =>
      api.resolveFeeCharge(vars.planId, vars.chargeId),
    onSuccess: (fees) => {
      setErr(null);
      setOk('Attempt closed off. The fee can be charged again.');
      setResolving(null);
      queryClient.setQueryData(['billing-fees'], fees);
    },
    onError: (e) => {
      setOk(null);
      setErr(e instanceof ApiRequestError ? e.message : 'That attempt could not be closed off.');
      setResolving(null);
      void queryClient.invalidateQueries({ queryKey: ['billing-fees'] });
    },
  });

  const cancelInvoice = useMutation({
    mutationFn: (vars: { planId: string; invoiceId: string }) =>
      api.cancelFeeInvoice(vars.planId, vars.invoiceId),
    onSuccess: (fees) => {
      setErr(null);
      setOk('Invoice cancelled. Nothing is owed for it.');
      setCancelling(null);
      queryClient.setQueryData(['billing-fees'], fees);
      void queryClient.invalidateQueries({ queryKey: ['billing-invoices'] });
    },
    onError: (e) => {
      setOk(null);
      setErr(e instanceof ApiRequestError ? e.message : 'That invoice could not be cancelled.');
      setCancelling(null);
      void queryClient.invalidateQueries({ queryKey: ['billing-fees'] });
    },
  });

  const fees = q.data ?? [];

  return (
    <Card>
      <CardHeader
        title="One-time fees"
        description="Charged to the tenant's saved card from here. These never appear on their Billing page."
      />
      <CardBody>
        {err && (
          <div className="mb-3">
            <AlertBanner tone="warning">{err}</AlertBanner>
          </div>
        )}
        {ok && (
          <div className="mb-3">
            <AlertBanner tone="info">{ok}</AlertBanner>
          </div>
        )}

        {q.isLoading ? (
          <p className="text-sm text-ink-faint">Loading…</p>
        ) : fees.length === 0 ? (
          <p className="text-sm text-ink-faint">
            No one-time products in Stripe yet. Create one, then use “Sync plans”.
          </p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {fees.map((fee) => (
              <li key={fee.id} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="block truncate font-medium text-ink">{fee.name}</span>
                    <span className="text-sm text-ink-label">{money(fee.amountCents, 'USD')}</span>
                    {fee.hasBeenPaid && (
                      <span className="ml-2 inline-block align-middle">
                        <Badge tone="green" size="sm">
                          Paid
                        </Badge>
                      </span>
                    )}
                  </div>
                  {/* Never disabled — it opens the dialog, which is where a refusal is explained. */}
                  <Button size="sm" onClick={() => setPendingFee(fee)}>
                    <CreditCard className="h-4 w-4" aria-hidden />
                    Apply / Charge
                  </Button>
                </div>

                {fee.gate.reason && (
                  <p
                    className={`mt-2 text-xs ${
                      fee.gate.state === 'REQUIRES_JUSTIFICATION' ? 'text-ink-muted' : 'text-amber-700 dark:text-warning-dark'
                    }`}
                  >
                    {fee.gate.state !== 'REQUIRES_JUSTIFICATION' && (
                      <Lock className="mr-1 inline h-3 w-3 align-[-1px]" aria-hidden />
                    )}
                    {fee.gate.reason}
                  </p>
                )}

                {fee.gate.outstanding.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {fee.gate.outstanding.map((invoice) => (
                      <li
                        key={invoice.id}
                        className="flex flex-wrap items-center gap-2 rounded-md bg-amber-50 dark:bg-warning-dark-tint px-2 py-1.5 text-xs"
                      >
                        <Badge tone="amber" size="sm">
                          {invoice.status === 'draft' ? 'Draft' : 'Unpaid'}
                        </Badge>
                        <span className="text-ink-label">
                          {money(invoice.amountDueCents, invoice.currency)} ·{' '}
                          {formatDateTimeLong(invoice.createdAt)}
                        </span>
                        {invoice.hostedInvoiceUrl && (
                          <a
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-link hover:text-action-hover hover:underline"
                          >
                            View
                          </a>
                        )}
                        {invoice.cancellable ? (
                          <button
                            type="button"
                            className="font-medium text-red-600 dark:text-error-dark hover:underline"
                            onClick={() => setCancelling({ fee, invoiceId: invoice.id })}
                          >
                            Cancel invoice
                          </button>
                        ) : (
                          // Voiding is irreversible and takes the whole invoice with it, so the
                          // button only acts on plain invoices this app raised for this fee.
                          <span className="text-ink-muted">
                            Not a plain invoice for this fee — cancel it in Stripe
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}

                {fee.charges.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {fee.charges.map((c) => (
                      <li key={c.id} className="flex flex-wrap items-center gap-2 text-xs">
                        <Badge tone={CHARGE_STATUS_TONE[c.status]} size="sm">
                          {CHARGE_STATUS_LABEL[c.status]}
                        </Badge>
                        <span className="text-ink-muted">
                          {money(c.amountCents, c.currency)} · {formatDateTimeLong(c.createdAt)}
                          {c.chargedBy ? ' · ' + c.chargedBy : ''}
                        </span>
                        {c.justification && (
                          <span className="text-ink-label">“{c.justification}”</span>
                        )}
                        {c.failureReason && <span className="text-red-600 dark:text-error-dark">{c.failureReason}</span>}
                        {c.hostedInvoiceUrl && (
                          <a
                            href={c.hostedInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="font-medium text-link hover:text-action-hover hover:underline"
                          >
                            Invoice
                          </a>
                        )}
                        {c.status === 'NEEDS_REVIEW' && (
                          // The way out when Stripe has no invoice for the attempt: the server
                          // re-checks and refuses if there is anything still collectable.
                          <button
                            type="button"
                            className="font-medium text-link hover:text-action-hover hover:underline"
                            onClick={() => setResolving({ fee, chargeId: c.id })}
                          >
                            Close this off
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>

      <ChargeFeeDialog
        fee={pendingFee}
        busy={charge.isPending}
        onClose={() => setPendingFee(null)}
        onConfirm={(justification) =>
          pendingFee && void hold(charge.mutateAsync({ planId: pendingFee.id, justification }))
        }
      />

      <ConfirmModal
        open={cancelling !== null}
        title="Cancel this invoice?"
        danger
        confirmLabel="Cancel invoice"
        cancelLabel="Keep it"
        busy={cancelInvoice.isPending}
        message={
          <>
            Stripe will void it and the tenant will owe nothing for it. This cannot be undone — a
            new charge would have to be raised.
          </>
        }
        onClose={() => setCancelling(null)}
        onConfirm={() =>
          cancelling &&
          void hold(cancelInvoice.mutateAsync({ planId: cancelling.fee.id, invoiceId: cancelling.invoiceId }))
        }
      />

      <ConfirmModal
        open={resolving !== null}
        title="Close off this attempt?"
        confirmLabel="Close it off"
        cancelLabel="Leave it"
        busy={resolveCharge.isPending}
        message={
          <>
            Only do this if the card was <strong>not</strong> charged. Stripe is checked again first:
            if it holds an unpaid invoice for this attempt you will be asked to cancel that instead,
            and if it turns out to have been paid the attempt is recorded as paid.
          </>
        }
        onClose={() => setResolving(null)}
        onConfirm={() =>
          resolving &&
          void hold(resolveCharge.mutateAsync({ planId: resolving.fee.id, chargeId: resolving.chargeId }))
        }
      />
    </Card>
  );
}

/**
 * The dialog between the button and the money.
 *
 * It states the amount before anything is charged, refuses outright while an attempt is unresolved
 * or an invoice is still collectable, and demands a written reason to repeat a fee that has already
 * been paid. None of this is trusted: the server applies the same rules to the request.
 */
function ChargeFeeDialog({
  fee,
  busy,
  onClose,
  onConfirm,
}: {
  fee: OneTimeFeeDto | null;
  busy: boolean;
  onClose: () => void;
  onConfirm: (justification?: string) => void;
}) {
  const [reason, setReason] = useState('');

  // Clear the reason whenever a different fee is opened, so a justification cannot be carried over
  // to a charge it was not written for.
  useEffect(() => {
    setReason('');
  }, [fee?.id]);

  if (!fee) return null;

  const blocked = fee.gate.state === 'IN_FLIGHT' || fee.gate.state === 'OUTSTANDING';
  const needsReason = fee.gate.state === 'REQUIRES_JUSTIFICATION';
  const reasonTooShort = needsReason && reason.trim().length < MIN_JUSTIFICATION_LENGTH;

  return (
    <Modal
      open
      onClose={onClose}
      title={blocked ? 'This fee cannot be charged yet' : `Charge ${fee.name}?`}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            {blocked ? 'Close' : 'Cancel'}
          </Button>
          {!blocked && (
            <Button
              size="sm"
              loading={busy}
              disabled={reasonTooShort}
              onClick={() => onConfirm(needsReason ? reason.trim() : undefined)}
            >
              <CreditCard className="h-4 w-4" aria-hidden />
              Charge {money(fee.amountCents, 'USD')}
            </Button>
          )}
        </div>
      }
    >
      {blocked ? (
        <div className="space-y-3">
          <AlertBanner tone="warning">{fee.gate.reason}</AlertBanner>
          {fee.gate.outstanding.length > 0 && (
            <p className="text-sm text-ink-label">
              Cancel the unpaid invoice listed on the fee, or let the tenant pay it, and this will
              unlock.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-ink-label">
            {money(fee.amountCents, 'USD')} will be charged to the card on file straight away and an
            invoice raised in Stripe.
          </p>
          {needsReason && (
            <>
              <AlertBanner tone="warning">{fee.gate.reason}</AlertBanner>
              <FormField label="Reason for charging this fee again" htmlFor="fee-justification">
                <Textarea
                  id="fee-justification"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Second onboarding for the Northwind brand workspace, agreed with the customer on 12 Aug."
                />
              </FormField>
              <p className="text-xs text-ink-faint">
                Recorded against the charge and printed on the tenant's invoice line.{' '}
                {reasonTooShort && `At least ${MIN_JUSTIFICATION_LENGTH} characters.`}
              </p>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
